import { Box, Chip, Stack, Typography } from '@mui/material';

import palette from 'src/theme/palette';

import { ProductThumb } from './ProductThumb';
import type { RetentionFirstPurchaseOrderIntensityStat } from './types';

function formatAvgAmongRepeaters(value: number | null): string | null {
  if (value === null || Number.isNaN(value) || !Number.isFinite(value))
    return null;
  return (Math.round(value * 10) / 10).toFixed(1);
}

export interface FirstPurchaseFollowUpOrdersListProps {
  data: RetentionFirstPurchaseOrderIntensityStat[];
  maxRows: number;
  showFooter?: boolean;
  /** `card`: altezza minima griglia; `modal`: lista scrollabile. */
  layout?: 'card' | 'modal';
}

export function FirstPurchaseFollowUpOrdersList({
  data,
  maxRows,
  showFooter = true,
  layout = 'card',
}: FirstPurchaseFollowUpOrdersListProps) {
  const rows = data.slice(0, maxRows);
  const maxTotal = Math.max(1, ...rows.map((r) => r.totalSubsequentOrders));

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Stack
        spacing={layout === 'modal' ? 1.35 : 1.75}
        sx={{ minHeight: layout === 'card' ? 280 : undefined }}
      >
        {rows.map((item, index) => {
          const avgAmongReturners = formatAvgAmongRepeaters(
            item.avgSubsequentOrdersPerFirstBuyer,
          );
          return (
            <Box
              key={`${item.productId}-${index}`}
              sx={{
                width: '100%',
                minWidth: 0,
                p: 1.25,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'rgba(15, 23, 42, 0.06)',
                bgcolor: 'rgba(248, 250, 252, 0.65)',
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    minWidth: 22,
                    minHeight: 40,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      color: palette.text.secondary,
                      fontWeight: 700,
                      lineHeight: 1,
                      fontSize: '0.95rem',
                    }}
                  >
                    {index + 1}
                  </Typography>
                </Box>

                <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
                  <ProductThumb
                    imageUrl={item.productImageUrl}
                    title={item.productTitle}
                    size={40}
                    radius={10}
                  />
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.35,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      width: '100%',
                      minWidth: 0,
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 0.5,
                          columnGap: 0.75,
                        }}
                      >
                        <Typography
                          component="span"
                          className="retention-pdf-unclamp"
                          sx={{
                            color: palette.text.primary,
                            fontWeight: 700,
                            lineHeight: 1.25,
                            fontSize: '0.88rem',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {item.productTitle}
                        </Typography>
                        {item.lowSample ? (
                          <Chip
                            label="Limited data"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor: 'rgba(245, 158, 11, 0.12)',
                              color: '#b45309',
                              border: '1px solid rgba(245, 158, 11, 0.35)',
                              flexShrink: 0,
                            }}
                          />
                        ) : null}
                      </Box>
                    </Box>

                    <Typography
                      component="div"
                      sx={{
                        flexShrink: 0,
                        textAlign: 'right',
                        fontWeight: 800,
                        fontSize: '0.95rem',
                        lineHeight: 1.2,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                        color: palette.text.primary,
                      }}
                    >
                      {item.totalSubsequentOrders}
                      {avgAmongReturners !== null ? (
                        <Box
                          component="span"
                          title="Average follow-up orders per customer who placed at least a second order"
                          sx={{
                            color: palette.text.secondary,
                            fontWeight: 600,
                            fontSize: '0.78rem',
                            ml: 0.35,
                          }}
                        >
                          · {avgAmongReturners} avg
                        </Box>
                      ) : null}
                    </Typography>
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: palette.text.secondary,
                      display: 'block',
                      lineHeight: 1.35,
                    }}
                    title={[
                      `${item.repeatCustomers} customers placed another order after buying this on their first order`,
                      `${item.firstPurchaseCustomers} customers had this product in their first order`,
                      `${item.totalSubsequentOrders} total orders placed after that first order (all follow-up orders from those first-time buyers)`,
                      avgAmongReturners !== null
                        ? `${avgAmongReturners} average follow-up orders per customer among the ${item.repeatCustomers} who came back`
                        : null,
                    ]
                      .filter((line): line is string => line !== null)
                      .join('. ')}
                  >
                    {item.repeatCustomers} came back ·{' '}
                    {item.firstPurchaseCustomers} had this in their first order
                    · {item.totalSubsequentOrders} orders after the first
                    {avgAmongReturners !== null ? (
                      <>
                        {' · '}
                        {avgAmongReturners} avg follow-up orders per customer
                        who came back
                      </>
                    ) : null}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 1.25,
                  height: 10,
                  borderRadius: 99,
                  bgcolor: '#eef2f7',
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    width: `${Math.max(6, Math.round((item.totalSubsequentOrders / maxTotal) * 100))}%`,
                    height: '100%',
                    borderRadius: 99,
                    background: item.lowSample
                      ? 'linear-gradient(90deg, rgba(129,140,248,0.55) 0%, rgba(99,102,241,0.9) 100%)'
                      : 'linear-gradient(90deg, rgba(99,102,241,0.75) 0%, rgba(79,70,229,1) 100%)',
                    boxShadow: item.lowSample
                      ? 'none'
                      : '0 8px 18px -12px rgba(99, 102, 241, 0.45)',
                  }}
                />
              </Box>
            </Box>
          );
        })}

        {showFooter ? (
          <Typography
            variant="caption"
            sx={{
              color: palette.text.secondary,
              pt: 0.5,
              lineHeight: 1.45,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            Sorted by total follow-up orders from first-time buyers who had this
            product in their first order. Ties favour a higher average of orders
            per buyer. Bar length is relative to the strongest row in the list
            above (not the whole store).
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

interface FirstPurchaseFollowUpOrdersChartProps {
  data: RetentionFirstPurchaseOrderIntensityStat[];
}

export function FirstPurchaseFollowUpOrdersChart({
  data,
}: FirstPurchaseFollowUpOrdersChartProps) {
  return (
    <FirstPurchaseFollowUpOrdersList data={data} maxRows={5} layout="card" />
  );
}

export default FirstPurchaseFollowUpOrdersChart;
